import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as complianceApi from "./api";
import type {
    CreateItemTypeInput,
    UpsertComplianceItemInput,
    VerifyItemInput,
    CreateFrameworkInput,
} from "./types";

const KEYS = {
    itemTypes: ["compliance", "item-types"] as const,
    profile: (userId: string) => ["compliance", "profile", userId] as const,
    orgSummary: ["compliance", "org-summary"] as const,
    gapReport: ["compliance", "gap-report"] as const,
    frameworks: ["compliance", "frameworks"] as const,
    framework: (id: string) => ["compliance", "frameworks", id] as const,
    controls: ["compliance", "controls"] as const,
    risks: ["compliance", "risks"] as const,
    audits: ["compliance", "audits"] as const,
};

export function useComplianceItemTypes(activeOnly = true) {
    return useQuery({
        queryKey: [...KEYS.itemTypes, activeOnly],
        queryFn: () => complianceApi.listItemTypes(activeOnly).then((r) => r.data ?? []),
    });
}

export function useCreateItemType() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (data: CreateItemTypeInput) =>
            complianceApi.createItemType(data).then((r) => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.itemTypes }),
    });
}

export function useUpdateItemType() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<CreateItemTypeInput> }) =>
            complianceApi.updateItemType(id, data).then((r) => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.itemTypes }),
    });
}

export function useEmployeeComplianceProfile(userId: string) {
    return useQuery({
        queryKey: KEYS.profile(userId),
        queryFn: () => complianceApi.getEmployeeProfile(userId).then((r) => r.data),
        enabled: !!userId,
    });
}

export function useSubmitComplianceItem() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ userId, data }: { userId: string; data: UpsertComplianceItemInput }) =>
            complianceApi.submitComplianceItem(userId, data).then((r) => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["compliance"] }),
    });
}

export function useVerifyItem() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: VerifyItemInput }) =>
            complianceApi.verifyItem(id, data).then((r) => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["compliance"] }),
    });
}

export function useOrgComplianceSummary() {
    return useQuery({
        queryKey: KEYS.orgSummary,
        queryFn: () => complianceApi.getOrgSummary().then((r) => r.data),
    });
}

export function useGapReport() {
    return useQuery({
        queryKey: KEYS.gapReport,
        queryFn: () => complianceApi.getGapReport().then((r) => r.data ?? []),
    });
}

// V2 Architecture

export function useFrameworks() {
    return useQuery({
        queryKey: KEYS.frameworks,
        queryFn: () => complianceApi.listFrameworks().then((r) => r.data ?? []),
    });
}

export function useCreateFramework() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (data: CreateFrameworkInput) =>
            complianceApi.createFramework(data).then((r) => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.frameworks }),
    });
}

export function useFramework(id: string) {
    return useQuery({
        queryKey: KEYS.framework(id),
        queryFn: () => complianceApi.getFramework(id).then((r) => r.data),
        enabled: !!id,
    });
}

export function useDeleteFramework() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) =>
            complianceApi.deleteFramework(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.frameworks }),
    });
}

export function useControls() {
    return useQuery({
        queryKey: KEYS.controls,
        queryFn: () => complianceApi.listControls().then((r) => r.data ?? []),
    });
}

export function useCreateControl() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (data: any) =>
            complianceApi.createControl(data).then((r) => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.controls }),
    });
}

export function useRisks() {
    return useQuery({
        queryKey: KEYS.risks,
        queryFn: () => complianceApi.listRisks().then((r) => r.data ?? []),
    });
}

export function useCreateRisk() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (data: any) =>
            complianceApi.createRisk(data).then((r) => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.risks }),
    });
}

export function useAudits() {
    return useQuery({
        queryKey: KEYS.audits,
        queryFn: () => complianceApi.listAudits().then((r) => r.data ?? []),
    });
}

export function useCreateAudit() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (data: any) =>
            complianceApi.createAudit(data).then((r) => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.audits }),
    });
}